/* DISPATCHER */
import { AppDispatcher } from 'dispatcher';

/* API */
import { VmsApi } from 'clients';

/* STORE */
import { BaseStore } from './BaseStore';

/* CONSTANTS */
import * as constants from 'vd-constants';

/* MISC */
import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import round from 'lodash/round';
import set from 'lodash/set';
import assign from 'object-assign';
import * as DateHandler from '../utils/DateHandler';
import DevicePollHandler from '../utils/DevicePollHandler';
import * as EventUtil from '../utils/EventUtil';

/**
 *
 * @type {DevicePollHandler}
 * @private
 */
const PollHandler = new DevicePollHandler();

/**
 *
 * @type {Object}
 * @private
 */
const _events = {};

/**
 *
 * @param {Object} event
 * @param {Record<string, CallhomeCamera>} siteCamerasById
 */
const initDownloadBookmark = function initDownloadBookmark(event, siteCamerasById) {
  const eventId = event.id;
  if (EventUtil.isDownloadBookmark(event) && !has(_events, eventId)) {
    _events[eventId] = {
      params: {},
      metaData: {
        streams: {},
      },
      status: {
        event: {},
        streams: {},
      },
    };

    // const start = DateHandler.getTimestamp(event.startTimeLocal);
    // const end = DateHandler.getTimestamp(event.endTimeLocal);
    const duration = (+new Date(event.endTime) - +new Date(event.startTime)) / 1000;

    forEach(get(event, ['cameras']), (camera) => {
      forEach(get(camera, ['streams']), (stream) => {
        // Add a 15% overhead size
        const calculatedSizeMB = (duration * stream.average_bps) / (8 * 1024 * 1024);
        set(
          _events,
          [eventId, 'metaData', 'streams', stream.id, 'calculatedSizeMB'],
          calculatedSizeMB + calculatedSizeMB * 0.15,
        );

        const index = findIndex(event.downloads, (download) => {
          return download.streamId === stream.id;
        });

        if (index !== -1) {
          const streamId = event.downloads[index].streamId;
          const finalState = event.downloads[index].state;
          const expiration = event.downloads[index].expiration;
          const filename = `(${camera.name}) ${DateHandler.toFilenameString(event.startTimeLocal)}`;
          const deviceId = siteCamerasById?.[camera.chId]?.deviceId;

          set(_events, [eventId, 'params', streamId], {
            deviceId,
            alias: `${eventId.replace(/-/g, '_')}_${streamId.replace(/-/g, '_')}`,
            filename,
            finalState,
            expired: !isNil(expiration) && DateHandler.getTimestamp() >= expiration,
          });
        }
      });
    });
  }
};

const getDownloadStatus = function getDownloadStatus(eventId, accessibleAddresses) {
  return new Promise((resolve) => {
    const requests = [];

    forEach(_events[eventId]['params'], (streamParams, streamId) => {
      const request = new Promise((innerResolve) => {
        if (
          streamParams.finalState === constants.CONNECT_DOWNLOAD_STATUS_FAILED ||
          streamParams.finalState === constants.CONNECT_DOWNLOAD_STATUS_CANCELLED
        ) {
          return innerResolve({
            streamId,
            expired: false,
            state: streamParams.finalState,
          });
        } else if (streamParams.expired) {
          return innerResolve({
            streamId,
            expired: true,
          });
        }
        // Get deviceId from stored params (set during initDownloadBookmark)
        const deviceId = streamParams.deviceId;
        const accessibleAddress = deviceId ? accessibleAddresses[deviceId] : null;

        if (!accessibleAddress || !deviceId) {
          return innerResolve({
            streamId,
            error: true,
          });
        }

        const connect = VmsApi(accessibleAddress);

        connect
          .getDownloadStatus(streamParams.alias)
          .then((result) => {
            if (isEmpty(result)) {
              // Download has expired
              _events[eventId]['params'][streamId]['expired'] = true;

              return innerResolve({
                streamId,
                expired: true,
              });
            }
            // Check whether the download has expired (i.e. JSON data is still available for a time even if download has expired)
            _events[eventId]['params'][streamId]['expired'] =
              !isNil(result.expiration) && DateHandler.getTimestamp() >= result.expiration;

            const filename = !isNil(result.format)
              ? `${streamParams.filename}.${result.format}`
              : null;

            return innerResolve({
              streamId,
              state: result.state,
              progress: result.progress,
              expiration: result.expiration,
              downloadCount: result.downloads_counter,
              downloadLink: `${accessibleAddress}${connect.generateDownloadVideoLink(
                streamParams.alias,
                filename,
              )}`,
              downloadFileName: filename,
              downloadSizeMB: result.estimated_size_mb,
            });
          })
          .catch((error) => {
            // Don't reject in case other downloads are fine
            if (error.statusCode === 404) {
              // Download has expired
              _events[eventId]['params'][streamId]['expired'] = true;

              return innerResolve({
                streamId,
                expired: true,
              });
            }
            console.error(`Error retrieving download status for "${streamParams.alias}"`, error);

            return innerResolve({
              streamId,
              error: true,
            });
          });
      });

      requests.push(request);
    });

    Promise.all(requests)
      .then((results) => {
        return resolve(results);
      })
      .catch((error) => {
        // Shouldn't be able to get here
        console.error('Error getting download status for streams', error);
        return resolve();
      });
  });
};

const processDownloadStatus = function processDownloadStatus(
  eventId,
  continueCallback,
  stopCallback,
  retryCallback,
  accessibleAddresses,
) {
  getDownloadStatus(eventId, accessibleAddresses)
    .then((results) => {
      // Stop if no results for event
      if (isEmpty(results)) {
        stopCallback();
        return;
      }

      let totalCount = 0;
      let totalProgress = 0;
      let hasBusy = false;
      let hasError = false;

      forEach(results, (downloadStatus) => {
        const streamId = downloadStatus.streamId;
        let streamPercent = calculatePercent(downloadStatus.progress, 1);
        let streamState = downloadStatus.state;

        switch (downloadStatus.state) {
          case constants.CONNECT_DOWNLOAD_STATUS_ENQUEUED:
          case constants.CONNECT_DOWNLOAD_STATUS_PROCESSING: {
            if (isNil(streamPercent)) {
              streamPercent = 0;
            }

            totalProgress += isNil(downloadStatus.progress) ? 0 : downloadStatus.progress;
            totalCount++;
            hasBusy = true;
            break;
          }
          case constants.CONNECT_DOWNLOAD_STATUS_READY:
          case constants.CONNECT_DOWNLOAD_STATUS_DOWNLOADED: {
            totalProgress += 1;
            totalCount++;
            break;
          }
          case constants.CONNECT_DOWNLOAD_STATUS_EXPIRED:
          case constants.CONNECT_DOWNLOAD_STATUS_CANCELLED:
          case constants.CONNECT_DOWNLOAD_STATUS_FAILED: {
            break;
          }
          default: {
            if (downloadStatus.expired) {
              streamState = constants.CONNECT_DOWNLOAD_STATUS_EXPIRED;
            } else if (downloadStatus.error) {
              hasError = true;
              streamState = constants.POLLING_DOWNLOAD_STATUS_ERROR;
            }
          }
        }

        _events[eventId]['status']['streams'][streamId] = {
          state: streamState,
          expiration: downloadStatus.expiration,
          downloadCount: downloadStatus.downloadCount || 0,
          downloadLink: downloadStatus.downloadLink,
          downloadFileName: downloadStatus.downloadFileName,
          downloadSizeMB: downloadStatus.downloadSizeMB,
          percent: streamPercent,
        };
      });

      const eventPercent = calculatePercent(totalProgress, totalCount);

      _events[eventId]['status']['event'] = {
        isProcessing: hasBusy,
        percent: eventPercent,
      };

      PollConnectStore.emitChange();

      if (hasBusy) {
        continueCallback();
      } else if (hasError) {
        // TODO rename
        retryCallback(constants.POLLING_DEFAULT_INTERVAL_MS);
      } else {
        stopCallback();
      }
    })
    .catch((error) => {
      // Shouldn't be able to get here
      console.error(`Error processing download status`, error);
    });
};

const calculatePercent = function calculatePercent(progress, streamCount) {
  let percent;

  if (!isNil(progress) && !isNil(streamCount) && streamCount > 0) {
    percent = progress / streamCount;

    percent = round(percent * 100, 0);

    // Shouldn't be able to exceed 100, but just in case
    percent = percent > 100 ? 100 : percent;
  }

  return percent;
};

// TODO: The 'event' and 'stream' can be the actual object or just id...
//       It's confusing but since a lot of legacy code depends on this,
//       we need a dedicate time to clean up
const getEventId = (event) => (isObject(event) ? get(event, 'id') : event);
const getStreamId = (stream) => (isObject(stream) ? get(stream, 'id') : stream);

const PollConnectStore = assign({}, BaseStore, {
  getEvents() {
    return _events;
  },

  /**
   * Get the progress of all streams of the event
   * @param {string} eventId - The event ID
   * @returns {number} percentage of the event
   */
  getEventProgress(eventId) {
    return get(_events, [eventId, 'status', 'event', 'percent']);
  },

  /**
   * Get the progress of specific stream of the event
   * @param {string} eventId - The event ID
   * @param {string} streamId - The stream ID
   * @returns {number} percentage of the event
   */
  getStreamProgress({ eventId, streamId }) {
    return get(_events, [eventId, 'status', 'streams', streamId, 'percent']);
  },

  getDownloadState(event, stream) {
    return get(_events, [getEventId(event), 'status', 'streams', getStreamId(stream), 'state']);
  },

  canCancel(event, stream) {
    const state = this.getDownloadState(event, stream);
    return state !== constants.CONNECT_DOWNLOAD_STATUS_CANCELLED;
    // return state === constants.CONNECT_DOWNLOAD_STATUS_ENQUEUED || state === constants.CONNECT_DOWNLOAD_STATUS_PROCESSING;
  },

  canDownload(event, stream) {
    const state = this.getDownloadState(event, stream);
    return (
      state === constants.CONNECT_DOWNLOAD_STATUS_READY ||
      state === constants.CONNECT_DOWNLOAD_STATUS_DOWNLOADED
    );
  },

  isProcessing(event, stream) {
    const state = this.getDownloadState(event, stream);
    return isNil(stream)
      ? get(_events, [getEventId(event), 'status', 'event', 'isProcessing'], false)
      : state === constants.CONNECT_DOWNLOAD_STATUS_ENQUEUED ||
          state === constants.CONNECT_DOWNLOAD_STATUS_PROCESSING;
  },

  /**
   *
   * @param {string|Object} event - Event ID or event object
   * @param {string|Object} stream - Stream ID or stream object
   * @returns {{link: string, alias: string, sizeMB: number, expiration: number, isExpired: boolean, state: string}}
   */
  getDownloadProperties(event, stream) {
    const state = this.getDownloadState(event, stream);
    const eventId = getEventId(event);
    const streamId = getStreamId(stream);
    const expiration = get(_events, [eventId, 'status', 'streams', streamId, 'expiration'], null);
    const calculatedSizeMB = get(_events, [
      eventId,
      'metaData',
      'streams',
      streamId,
      'calculatedSizeMB',
    ]);
    const estimatedSizeMB = get(
      _events,
      [eventId, 'status', 'streams', streamId, 'downloadSizeMB'],
      calculatedSizeMB,
    );
    const percent = this.getStreamProgress({ eventId, streamId });

    return {
      link: get(_events, [eventId, 'status', 'streams', streamId, 'downloadLink']),
      count: get(_events, [eventId, 'status', 'streams', streamId, 'downloadCount']),
      alias: get(_events, [eventId, 'params', streamId, 'alias'], ''),
      fileName: get(_events, [eventId, 'status', 'streams', streamId, 'downloadFileName']),
      sizeMB: estimatedSizeMB,
      expiration,
      isExpired: state === constants.CONNECT_DOWNLOAD_STATUS_EXPIRED,
      state,
      percent,
    };
  },
});

PollConnectStore.dispatchToken = AppDispatcher.register((action) => {
  switch (action.type) {
    case constants.CONNECT_POLL_START: {
      const accessibleAddresses = action.accessibleAddresses;
      const siteCamerasById = action.siteCamerasById;
      initDownloadBookmark(action.event, siteCamerasById);
      const eventId = action.event.id;
      const eventCameras = action.event.cameras;
      PollHandler.startPollingEvent(
        eventId,
        eventCameras,
        accessibleAddresses,
        siteCamerasById,
        processDownloadStatus,
      );
      break;
    }
    case constants.CONNECT_POLL_STOP: {
      PollHandler.stopPollingEvent(action.event.id, action.event.cameras, action.siteCamerasById);
      PollConnectStore.emitChange();
      break;
    }
  }
});

export default PollConnectStore;
